import type { SurveyPreset } from '@citizen-walk/shared-types';

/**
 * アンケートテンプレ (SurveyPreset) 単位の JSON export / import helper (AW8、docs/108 High3 master 側)。
 *
 * 旧2 `lib/surveys.ts::buildSurveyExport / parseSurveyExport` の master 版。v2 では master は
 * `surveyPresets` collection なので exportType を 'surveyPreset' に分け、地区 Survey の export
 * (exportType: 'survey') と混同しないようにする。
 *
 * export では監査 / 所有 / archive / version / id を strip し、import 時に新規採番させる。
 */

export interface SurveyPresetExport {
  exportType: 'surveyPreset';
  exportedAt: string;
  version: 1;
  preset: Pick<
    SurveyPreset,
    | 'displayName'
    | 'mode'
    | 'title'
    | 'description'
    | 'status'
    | 'collectParticipantProfile'
    | 'allowEdit'
    | 'questions'
  >;
}

/** SurveyPreset から export envelope を生成。 */
export function buildSurveyPresetExport(preset: SurveyPreset & { id: string }): SurveyPresetExport {
  return {
    exportType: 'surveyPreset',
    exportedAt: new Date().toISOString(),
    version: 1,
    preset: {
      displayName: preset.displayName,
      mode: preset.mode,
      title: preset.title ?? '',
      description: preset.description ?? '',
      status: preset.status ?? 'draft',
      collectParticipantProfile: preset.collectParticipantProfile ?? true,
      allowEdit: preset.allowEdit ?? true,
      questions: preset.questions ?? [],
    },
  };
}

/** export JSON 文字列を parse + 妥当性検証。失敗時は日本語メッセージで throw。 */
export function parseSurveyPresetExport(text: string): SurveyPresetExport {
  let parsed: Partial<SurveyPresetExport>;
  try {
    parsed = JSON.parse(text) as Partial<SurveyPresetExport>;
  } catch {
    throw new Error('JSON の解析に失敗しました');
  }
  if (parsed.exportType !== 'surveyPreset' || !parsed.preset) {
    throw new Error('アンケートテンプレの設定ファイルではありません');
  }
  if (!Array.isArray(parsed.preset.questions)) {
    throw new Error('questions が配列ではありません');
  }
  return parsed as SurveyPresetExport;
}

/** export 用ファイル名 `<displayName|id>_yyyy-mm-dd.json`。 */
export function buildSurveyPresetExportFilename(
  preset: SurveyPreset & { id: string },
  now: Date = new Date(),
): string {
  const base = (preset.displayName || preset.id).replace(/[^\w\-一-龯ぁ-ゔァ-ヴー]/g, '_');
  return `survey-preset_${base}_${now.toISOString().slice(0, 10)}.json`;
}

/** Blob → ObjectURL → <a download> でブラウザ DL (JSON、BOM 無し)。 */
export function downloadPresetJson(filename: string, obj: unknown): void {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
